import { useState, useEffect } from "react";
import { FORMAT_DAY_TIME, ONE_HOUR_OF_MS } from "utils/timeUtils";
import {
  IconButton,
  Button,
  Tooltip,
  Collapse,
  Popper,
  Paper,
  Fade,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import CompleteTheLotIcon from "@mui/icons-material/AssignmentTurnedIn";
import WarningIcon from "@mui/icons-material/Warning";
import {
  LotInfoBarStyles,
  TwoRowsStyles,
  ForecastedLotCompletionTitleStyles,
  HasExceededLotSizeWarningBannerStyles,
} from "./LotInfoBarStyles";
import { useLotInfoForProcess, useLotText } from "./lotInfoUtils";
import CompleteTheLotModal from "./CompleteTheLotModal";
import useContainerDimensions from "utils/useContainerDimensions";
import type { LotInfoType } from "types/types";
import { isEqual, startCase } from "lodash";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import {
  LotNumberAndNextLotNumber,
  LotSizeAndNextLotSize,
} from "./LotSizeLotNumber";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { useForecastedLotCompletionDateMs } from "./useForecastedLotCompletionDateMs";
import {
  cardBorder,
  darkModeCardBorderColor,
  highlightOnBlack,
} from "constants/colors";
import { HelpButton } from "./HelpButton";
import { getIsTouchDevice } from "utils/getIsTouchDevice";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFormatZoned, useInterval } from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import {
  CUSTOMERS_MAX_LOT_DURATION,
  CUSTOMERS_WHO_HIDE_ESTIMATED_LOT_COMPLETION,
} from "constants/customers";
import { useIsDarkMode, useIsLotInfoPaused } from "store/atoms";
import { useBreakpoints, Z_INDEX } from "constants/constants";
import { PausedOverlay } from "./PausedOverlay";

// typescript error - Timeline missing "onResize, onResizeCapture" https://github.com/mui/material-ui/issues/35287#issuecomment-1337250566
declare global {
  /* eslint-disable @typescript-eslint/no-namespace */
  namespace React {
    interface DOMAttributes<T> {
      onResize?: ReactEventHandler<T> | undefined;
      onResizeCapture?: ReactEventHandler<T> | undefined;
    }
  }
}

const WAITING_FOR_COUNTS = "Waiting_for_counts";
const getLotIsCompleteText = (lotText: string) =>
  `${startCase(lotText).replace(" ", "_")}_size_achieved`;

const LotInfoBar = () => {
  const {
    lotInfoForProcess: lotInfo,
    submitEditedLotInfo,
    completeTheLot,
    loading: isLoading,
  } = useLotInfoForProcess();
  const forecastedLotCompletionDateMs = useForecastedLotCompletionDateMs();

  return (
    <LotInfoBarContent
      lotInfo={lotInfo}
      isLoading={isLoading}
      submitEditedLotInfo={submitEditedLotInfo}
      completeTheLot={completeTheLot}
      forecastedLotCompletionDateMs={forecastedLotCompletionDateMs}
    />
  );
};

type LotInfoBarContentProps = {
  lotInfo: LotInfoType;
  forecastedLotCompletionDateMs: number | null;
  isLoading: boolean;
  submitEditedLotInfo: (lotInfoEdited: LotInfoType) => Promise<any>;
  completeTheLot: () => Promise<any>;
};

// tslint:disable-next-line
export function LotInfoBarContent({
  lotInfo,
  submitEditedLotInfo,
  isLoading,
  forecastedLotCompletionDateMs,
  completeTheLot,
}: LotInfoBarContentProps) {
  const ckey = useCustomerKey();
  const lotText = useLotText();
  const formatZoned = useFormatZoned();

  // if forecast is Infinity that means count / rate is 0
  // (null forecast means we're still waiting for data)
  const isStillWaitingForCounts = forecastedLotCompletionDateMs === Infinity;

  // has count exceeded the lot size? show complete the lot warning
  const hasCountExceededLotSize = forecastedLotCompletionDateMs === -Infinity;

  const { lotNumber, nextLotNumber, lotSize, nextLotSize } = lotInfo;

  const [isPaused, setIsPaused] = useIsLotInfoPaused();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCompletingTheLot, setIsCompletingTheLot] = useState(false);
  const [editedLotInfo, setEditedLotInfo] = useState(lotInfo);

  const { translate } = useFlightTranslation();

  // update the edited lot info when stored lot info changes
  useEffect(() => {
    if (!isEditMode) {
      setEditedLotInfo(lotInfo);
    }
  }, [lotInfo, isEditMode]);

  const isStorybook = useIsStorybook();
  const now =
    isStorybook && ckey !== "ravendemo"
      ? new Date("January 1 2020 18:30").getTime()
      : Date.now();

  const numHoursUntilCompletion =
    ((forecastedLotCompletionDateMs || 0) - now) / ONE_HOUR_OF_MS;

  const forecastedLotCompletionFormatted = hasCountExceededLotSize
    ? translate(
        getLotIsCompleteText(lotText),
        `${startCase(lotText)} size achieved!`
      )
    : isStillWaitingForCounts
      ? translate(WAITING_FOR_COUNTS)
      : forecastedLotCompletionDateMs
        ? CUSTOMERS_WHO_HIDE_ESTIMATED_LOT_COMPLETION.includes(ckey)
          ? `${numHoursUntilCompletion.toFixed(0)}h`
          : `${formatZoned(
              forecastedLotCompletionDateMs,
              FORMAT_DAY_TIME
            )} (${numHoursUntilCompletion.toFixed(0)}h)`
        : "--";

  const handleNumberInputChange = event => {
    event.persist(); // React disposes synthetic events for performance, need to "persist" it
    const nextValue = Number(event.target.value);
    if (!isNaN(nextValue) && !String(nextValue).includes("e")) {
      setEditedLotInfo(prevState => ({
        ...prevState,
        [event.target.name]: nextValue,
      }));
    }
  };
  const handleTextInputChange = event => {
    event.persist(); // React disposes synthetic events for performance, need to "persist" it
    setEditedLotInfo(prevState => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  };
  const handleConfirmChanges = e => {
    e.stopPropagation();
    setIsEditMode(false);
    setIsPaused(false);
    // update the *currently displayed* lot info
    if (!isEqual(editedLotInfo, lotInfo)) {
      submitEditedLotInfo(editedLotInfo);
    }
  };
  const handleStartEditing = e => {
    e.stopPropagation();
    setIsEditMode(true);
  };
  const handleOpenCompleteTheLotDialog = e => {
    e.stopPropagation();
    setIsDialogOpen(true);
  };
  const handleCloseCompleteTheLotDialog = () => setIsDialogOpen(false);
  const handleCancelEditing = e => {
    e.stopPropagation();
    // turn off edit mode & reset all editedLotInfo back to current lotInfo
    setIsEditMode(false);
    setEditedLotInfo(lotInfo);
  };
  const handleCompleteTheLot = () => {
    // submit the lot to the back-end and start the next one
    setIsCompletingTheLot(true);
    completeTheLot()
      .then(() => {
        setIsCompletingTheLot(false);
        setIsDialogOpen(false);
      })
      .catch(err => {
        console.error(err);
        console.log(JSON.stringify(err));
        setIsDialogOpen(false);
      });
  };

  // when we go over the maxLotDuration,
  // complete the lot, and pause lot tracking
  const maxLotDuration =
    CUSTOMERS_MAX_LOT_DURATION[ckey] || CUSTOMERS_MAX_LOT_DURATION.DEFAULT;
  useInterval({
    callback: () => {
      if (isStorybook || isPaused || isEditMode || !lotInfo.lotStartDateMs) {
        return;
      }
      const lotDuration = now - lotInfo.lotStartDateMs;

      const isMaxDurationExceeded = lotDuration > maxLotDuration;

      if (isMaxDurationExceeded) {
        setIsPaused(true);
        handleCompleteTheLot();
      }
    },
    delay: isStorybook || isPaused || isEditMode ? null : 30 * 1000,
    immediate: isStorybook || (!isPaused && !isEditMode),
  });

  const [ref, dimensions] = useContainerDimensions();
  const width = dimensions.width || 0;

  // don't let user complete the lot unless all fields are filled in
  const isCompleteTheLotDisabled =
    !lotNumber || !lotSize || !nextLotNumber || !nextLotSize;

  const isLotNumberError = Boolean(
    editedLotInfo?.lotNumber &&
      // error: lot number is the same as the next lot number
      editedLotInfo?.lotNumber === editedLotInfo?.nextLotNumber
  );

  const isError = isEditMode && isLotNumberError; // potentially more error states in future

  // don't let user confirm unless at least current fields are filled in
  const isConfirmDisabled =
    isError || !editedLotInfo.lotNumber || !editedLotInfo.lotSize;

  // number of hours ago that the lot started
  const waitingForLotInfo = !lotInfo.lotStartDateMs;
  const timeAgoLotStarted = lotInfo.lotStartDateMs
    ? now - lotInfo.lotStartDateMs
    : Infinity;
  const hoursAgo = isStorybook
    ? 4242
    : (timeAgoLotStarted / ONE_HOUR_OF_MS).toFixed(0);

  const TOOLTIP_TEXT_ROW_1 = translate(
    `Complete_the_${startCase(lotText).replace(" ", "_")}`
  );

  const TOOLTIP_TEXT_ROW_2 = translate(
    `Next_${lotText.replace(" ", "_")}_info_must_be_filled_in`
  );

  const isTouchDevice = getIsTouchDevice();
  const [isDarkMode] = useIsDarkMode();

  const { isTabletOrLarger } = useBreakpoints();
  const forecast = (
    <ForecastedLotCompletion
      {...{
        waitingForLotInfo,
        isStillWaitingForCounts,
        forecastedLotCompletionFormatted,
        lotText,
        hoursAgo,
      }}
    />
  );

  const [isMobileForecastOpen, setIsMobileForecastOpen] = useState(false);

  return (
    <LotInfoBarStyles
      ref={ref}
      $isError={isError}
      $isPaused={isPaused}
      $isEditMode={isEditMode}
      className={isTouchDevice ? "isTouchDevice" : ""}
      data-component-name={`${startCase(lotText)} Info Bar`}
      $isDarkMode={isDarkMode}
      $hasCountExceededLotSize={hasCountExceededLotSize}
      onClick={() => {
        if (!isTabletOrLarger) {
          setIsMobileForecastOpen(!isMobileForecastOpen);
        }
      }}
    >
      {isPaused && (
        <PausedOverlay
          {...{
            handleResume: () => {
              setIsPaused(false);
              setIsEditMode(true);
              handleCompleteTheLot();
            },
          }}
        />
      )}
      <HelpButton />
      <LotNumberAndNextLotNumber
        width={width}
        isEditMode={isEditMode}
        editedLotInfo={editedLotInfo}
        handleTextInputChange={handleTextInputChange}
        isLoading={isLoading}
        isLotNumberError={isLotNumberError}
        lotInfo={lotInfo}
      />
      {isTabletOrLarger ? (
        forecast
      ) : ref.current && !isStorybook ? (
        <Popper
          open={true}
          anchorEl={ref.current}
          style={{
            zIndex: Z_INDEX.L1,
          }}
        >
          <Fade in={isMobileForecastOpen}>
            <Paper
              elevation={4}
              style={{
                padding: 12,
                border: `1px solid ${
                  isDarkMode ? darkModeCardBorderColor : cardBorder
                }`,
              }}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              {forecast}
            </Paper>
          </Fade>
        </Popper>
      ) : null}
      <LotSizeAndNextLotSize
        lotInfo={lotInfo}
        isLoading={isLoading}
        isEditMode={isEditMode}
        editedLotInfo={editedLotInfo}
        handleNumberInputChange={handleNumberInputChange}
        width={width}
      />
      {isEditMode ? (
        <div className="btnsConfirmCancelWrapper">
          <IconButton
            className="btn btnConfirm"
            onClick={handleConfirmChanges}
            disabled={isConfirmDisabled}
            size="large"
          >
            <DoneIcon />
          </IconButton>
          <IconButton
            className="btn btnCancel"
            onClick={handleCancelEditing}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </div>
      ) : (
        <div className="defaultActions">
          <Button
            variant="outlined"
            className="btn btnEdit"
            disabled={isLoading}
            onClick={handleStartEditing}
          >
            {translate("EDIT")}
          </Button>
          {isStorybook ? (
            <IconButton
              className="btn btnCompleteLot"
              disabled={isCompleteTheLotDisabled}
              onClick={handleOpenCompleteTheLotDialog}
              size="large"
            >
              <CompleteTheLotIcon />
            </IconButton>
          ) : (
            <Tooltip
              title={
                <div style={{ textAlign: "center" }}>
                  <div>{TOOLTIP_TEXT_ROW_1}</div>
                  {isCompleteTheLotDisabled && (
                    <div
                      style={{ color: highlightOnBlack, fontWeight: "bold" }}
                    >
                      {TOOLTIP_TEXT_ROW_2}
                    </div>
                  )}
                </div>
              }
            >
              <div className="btnCompleteTheLotWrapper">
                <IconButton
                  className="btn btnCompleteLot"
                  disabled={isCompleteTheLotDisabled}
                  onClick={handleOpenCompleteTheLotDialog}
                  size="large"
                >
                  <CompleteTheLotIcon />
                </IconButton>
              </div>
            </Tooltip>
          )}
        </div>
      )}
      <CompleteTheLotModal
        isCompletingTheLot={isCompletingTheLot}
        isDialogOpen={isDialogOpen}
        lotInfo={lotInfo}
        handleCloseCompleteTheLotDialog={handleCloseCompleteTheLotDialog}
        handleCompleteTheLot={handleCompleteTheLot}
      />
      <HasExceededLotSizeWarningBanner
        hasCountExceededLotSize={hasCountExceededLotSize}
      />
    </LotInfoBarStyles>
  );
}

export default LotInfoBar;

function HasExceededLotSizeWarningBanner({
  hasCountExceededLotSize,
}: {
  hasCountExceededLotSize: boolean;
}) {
  const lotText = useLotText();
  const { translate } = useFlightTranslation();
  return (
    <HasExceededLotSizeWarningBannerStyles>
      <Collapse in={hasCountExceededLotSize}>
        <div className="bannerContent">
          <WarningIcon />{" "}
          {translate(
            `Current_count_has_exceeded_the_${lotText.replace(" ", "_")}_size`,
            `Current count has exceeded the ${lotText} size --`
          )}
          {translate(
            `please_press_Complete_the_${startCase(lotText).replace(" ", "_")}`
          )}
          <CompleteTheLotIcon />
        </div>
      </Collapse>
    </HasExceededLotSizeWarningBannerStyles>
  );
}

function ForecastedLotCompletion({
  waitingForLotInfo,
  isStillWaitingForCounts,
  forecastedLotCompletionFormatted,
  lotText,
  hoursAgo,
}: {
  waitingForLotInfo: boolean;
  isStillWaitingForCounts: boolean;
  forecastedLotCompletionFormatted: string;
  lotText: string;
  hoursAgo: string | number;
}) {
  const { translate } = useFlightTranslation();
  const [isDarkMode] = useIsDarkMode();
  const LOT_STATUS = translate("lot_status", "", {
    hours: hoursAgo,
  });

  const FETCHING_INFO = translate(
    `fetching_${lotText}_info`,
    `(fetching ${lotText} info...)`
  );
  const hoursAgoContent = (
    <div className="hoursAgo">
      {waitingForLotInfo ? FETCHING_INFO : LOT_STATUS}
    </div>
  );
  const { isTabletOrLarger } = useBreakpoints();
  return (
    <TwoRowsStyles $isDarkMode={isDarkMode}>
      <ForecastedLotCompletionTitleStyles
        className="row row-top"
        $isDarkMode={isDarkMode}
      >
        {translate(
          `Forecasted_${startCase(lotText).replace(" ", "_")}_Completion`
        )}
        {isTabletOrLarger ? hoursAgoContent : null}
      </ForecastedLotCompletionTitleStyles>
      {isTabletOrLarger ? null : hoursAgoContent}
      <div
        className={`row row-bottom${isStillWaitingForCounts ? " small" : ""}`}
        data-server-names={[SERVER_NAMES.midgardLotCounts]}
      >
        {forecastedLotCompletionFormatted}
      </div>
    </TwoRowsStyles>
  );
}
